import React, { useState, useRef, useEffect } from "react";
import "./SafetyDashboard.css";
import {
  submitAnalysis,
  pollJob,
  downloadReport,
  getVideoUrl,
  generateVideo,
  checkHealth,
} from "../services/api";

function SafetyDashboard() {
  const [pdfFile,       setPdfFile]       = useState(null);
  const [videoFile,     setVideoFile]     = useState(null);
  const [fileName,      setFileName]      = useState("");
  const [videoFileName, setVideoFileName] = useState("");
  const [job,           setJob]           = useState(null);
  const [violations,    setViolations]    = useState([]);
  const [agentLogs,     setAgentLogs]     = useState([]);
  const [status,        setStatus]        = useState(null);
  const [running,       setRunning]       = useState(false);
  const [activeTab,     setActiveTab]     = useState("violations");
  const [isDraggingPdf, setIsDraggingPdf] = useState(false);
  const [isDraggingVid, setIsDraggingVid] = useState(false);
  const [manualJobId,   setManualJobId]   = useState(null);
  const [loadingManual, setLoadingManual] = useState(false);
  const [pipelineSubmitted, setPipelineSubmitted] = useState(false);

  const pdfInputRef = useRef(null);
  const vidInputRef = useRef(null);

  // ── health check on mount (silent) ──────────────────────────────────────
  useEffect(() => {
    checkHealth().then((ok) => {
      if (!ok) {
        console.warn("Backend connection failed");
      }
    });
  }, []);

  // ── auto-run pipeline when both files ready ───────────────────────────
  useEffect(() => {
    if (pdfFile && videoFile && !running && !pipelineSubmitted && !job) {
      setPipelineSubmitted(true);
      runPipeline(videoFile, pdfFile);
    }
  }, [pdfFile, videoFile, running, pipelineSubmitted, job]);

  // ── build summary from violations ─────────────────────────────────────
  const buildSummary = (vs = []) => {
    const critical = vs.filter(
      (v) => v.severity === "high" || v.severity === "critical"
    ).length;
    return {
      total:          vs.length,
      critical,
      complianceRate: vs.length
        ? Math.round(((vs.length - critical) / vs.length) * 100)
        : 100,
    };
  };

  // ── pipeline ──────────────────────────────────────────────────────────
  const runPipeline = async (vid, pdf) => {
    setRunning(true);
    setJob(null);
    setViolations([]);
    setAgentLogs([]);
    setManualJobId(null);
    setStatus({ type: "info", message: "Uploading files to pipeline..." });

    try {
      const { job_id } = await submitAnalysis(vid, pdf);
      setStatus({ type: "info", message: "Job created — agents running..." });

      await pollJob(job_id, (updated) => {
        setJob(updated);
        setAgentLogs(updated.logs || []);

        if (updated.status === "processing")
          setStatus({
            type: "info",
            message: "Pipeline running — vision + rules agents active...",
          });

        if (updated.status === "completed") {
          setViolations(updated.violations || []);
          setActiveTab("violations");
          setStatus({
            type: "success",
            message: `completed — ${updated.violations?.length ?? 0} violation(s) found.`,
          });
        }

        if (updated.status === "failed")
          setStatus({
            type: "error",
            message: `Pipeline failed: ${(updated.errors || []).join(", ")}`,
          });
      });
    } catch (err) {
      setStatus({ type: "error", message: `Error: ${err.message}` });
    } finally {
      setRunning(false);
    }
  };

  // ── file handlers ─────────────────────────────────────────────────────
  const onPdfChange = (file) => {
    if (!file || file.type !== "application/pdf")
      return alert("Please upload a valid PDF file");
    setFileName(file.name);
    setPdfFile(file);
    setPipelineSubmitted(false);
  };

  const onVideoChange = (file) => {
    if (!file || !file.type.startsWith("video/"))
      return alert("Please upload a valid video file (MP4, AVI, MOV)");
    setVideoFileName(file.name);
    setVideoFile(file);
    setPipelineSubmitted(false);
  };

  // ── generate video button ─────────────────────────────────────────────
  const handleGenerateVideo = async () => {
    setLoadingManual(true);
    setStatus({ type: "info", message: "VideoGenAgent loading output video..." });
    try {
      const isDone = job?.status === "completed";
      if (isDone && job?.job_id) {
        setActiveTab("video");
        setStatus({ type: "success", message: "VideoGenAgent generated video successfully!" });
        return;
      }
      const result = await generateVideo();
      setManualJobId(result.job_id);
      setActiveTab("video");
      setStatus({ type: "success", message: "VideoGenAgent generated video successfully!" });
    } catch (err) {
      setStatus({ type: "error", message: `Failed: ${err.message}` });
    } finally {
      setLoadingManual(false);
    }
  };

  // ── download report ───────────────────────────────────────────────────
  const handleDownloadReport = async () => {
    if (!job) return;
    setStatus({ type: "info", message: "Downloading report..." });
    try {
      await downloadReport(job.job_id);
      setStatus({ type: "success", message: "Report downloaded!" });
    } catch {
      const rows = violations.map((v) =>
        [v.rule_id || "-", v.severity || "-", v.frame ?? "-", v.description || ""].join(",")
      );
      const csv  = ["rule_id,severity,frame,description", ...rows].join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `violations_${job?.job_id || Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      setStatus({ type: "success", message: "Downloaded as CSV (fallback)." });
    }
  };

  // ── reset ─────────────────────────────────────────────────────────────
  const reset = () => {
    setPdfFile(null);
    setVideoFile(null);
    setFileName("");
    setVideoFileName("");
    setJob(null);
    setViolations([]);
    setAgentLogs([]);
    setManualJobId(null);
    setPipelineSubmitted(false);
    setStatus(null);
    setActiveTab("violations");
    if (pdfInputRef.current) pdfInputRef.current.value = "";
    if (vidInputRef.current) vidInputRef.current.value = "";
  };

  const isDone   = job?.status === "completed";
  const isFailed = job?.status === "failed";
  const summary  = buildSummary(violations);
  const videoJobId = job?.job_id || manualJobId;

  const getSafeVideoUrl = () => {
    return videoJobId ? getVideoUrl(videoJobId) : "";
  };

  return (
    <div className="safety-dashboard">

      {/* Header */}
      <div className="dashboard-header">
        <div className="header-badge">
          <span className="badge-icon"></span>
          <span>Enterprise Safety Platform</span>
        </div>
        <h2>Upload your safety documents and download professional compliance reports</h2>
      </div>

      {/* Upload cards */}
      <div className="upload-sections">

        {/* PDF card */}
        <div
          className={`upload-card ${isDraggingPdf ? "dragging" : ""}`}
          onDragOver={(e)  => { e.preventDefault(); setIsDraggingPdf(true);  }}
          onDragLeave={(e) => { e.preventDefault(); setIsDraggingPdf(false); }}
          onDrop={(e) => {
            e.preventDefault();
            setIsDraggingPdf(false);
            onPdfChange(e.dataTransfer.files[0]);
          }}
        >
          <div className="upload-card-header">
            <div className="upload-icon-wrapper">
              <div className="upload-icon">📄</div>
            </div>
            <div className="upload-info">
              <h3>Upload Safety Rules PDF</h3>
              <p>Drag & drop or click to upload</p>
            </div>
          </div>
          <div className="upload-area">
            <input
              type="file" accept=".pdf"
              ref={pdfInputRef} id="pdf-upload"
              disabled={running}
              onChange={(e) => onPdfChange(e.target.files[0])}
              style={{ display: "none" }}
            />
            <label htmlFor="pdf-upload" className="upload-label">
              <span className="upload-icon-small">📁</span>
              {fileName || "Choose PDF File"}
            </label>
            <p className="upload-hint">Supported format: PDF (Max 50MB)</p>
            {pdfFile && !running && (
              <p style={{ color: "#4ade80", fontSize: 12, marginTop: 6 }}>
                Ready: {fileName}
              </p>
            )}
          </div>
        </div>

        {/* Video card */}
        <div
          className={`upload-card ${isDraggingVid ? "dragging" : ""}`}
          onDragOver={(e)  => { e.preventDefault(); setIsDraggingVid(true);  }}
          onDragLeave={(e) => { e.preventDefault(); setIsDraggingVid(false); }}
          onDrop={(e) => {
            e.preventDefault();
            setIsDraggingVid(false);
            onVideoChange(e.dataTransfer.files[0]);
          }}
        >
          <div className="upload-card-header">
            <div className="upload-icon-wrapper">
              <div className="upload-icon">🎥</div>
            </div>
            <div className="upload-info">
              <h3>Upload Training Video</h3>
              <p>Drag & drop or click to upload</p>
            </div>
          </div>
          <div className="upload-area">
            <input
              type="file" accept="video/*"
              ref={vidInputRef} id="video-upload"
              disabled={running}
              onChange={(e) => onVideoChange(e.target.files[0])}
              style={{ display: "none" }}
            />
            <label htmlFor="video-upload" className="upload-label">
              <span className="upload-icon-small">🎬</span>
              {videoFileName || "Choose Video File"}
            </label>
            <p className="upload-hint">Supported formats: MP4, AVI, MOV (Max 200MB)</p>
            {videoFile && !running && (
              <p style={{ color: "#4ade80", fontSize: 12, marginTop: 6 }}>
                Ready: {videoFileName}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Hint when only one file uploaded */}
      {pdfFile && !videoFile && !running && (
        <p style={{ textAlign: "center", color: "#94a3b8", marginTop: 8 }}>
          Now upload the surveillance video to start the pipeline.
        </p>
      )}
      {videoFile && !pdfFile && !running && (
        <p style={{ textAlign: "center", color: "#94a3b8", marginTop: 8 }}>
          Now upload the PDF rules file to start the pipeline.
        </p>
      )}

      {/* Running indicator */}
      {running && (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div className="progress-bar" style={{ maxWidth: 400, margin: "0 auto" }}>
            <div
              className="progress-fill"
              style={{ width: "100%", animation: "pulse 1.5s ease-in-out infinite" }}
            />
          </div>
          <p style={{ marginTop: 8, color: "#94a3b8" }}>
            running ,please wait...
          </p>
        </div>
      )}

      {/* Status message */}
      {status && (
        <div className={`status-message ${status.type} slide-down`}>
          <span className="status-icon">
            {status.type === "success" ? "✅"
              : status.type === "error" ? "❌ ⚠️"
              : "🔄"}
          </span>
          <span>{status.message}</span>
        </div>
      )}

      {/* Results */}
      {(isDone || manualJobId) && (
        <div className="results-section fade-in-up">

          {/* Stats grid - 3 cards only (removed Agent Steps) */}
          {isDone && (
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">📊</div>
                <div className="stat-number">{summary.total}</div>
                <div className="stat-label">Total Violations</div>
              </div>
              <div className="stat-card warning">
                <div className="stat-icon">⚠️</div>
                <div className="stat-number">{summary.critical}</div>
                <div className="stat-label">Critical</div>
              </div>
              <div className="stat-card success">
                <div className="stat-icon">📈</div>
                <div className="stat-number">{summary.complianceRate}%</div>
                <div className="stat-label">Compliance</div>
              </div>
            </div>
          )}

          {/* Tabs - 3 tabs only (removed Agent Logs) */}
          <div className="results-tabs">
            {isDone && (
              <button
                className={activeTab === "violations" ? "tab-active" : "tab"}
                onClick={() => setActiveTab("violations")}
              >
                <span className="tab-icon">📋</span>
                Violations
                <span className="tab-badge">{violations.length}</span>
              </button>
            )}
            {videoJobId && (
              <button
                className={activeTab === "video" ? "tab-active" : "tab"}
                onClick={() => setActiveTab("video")}
              >
                <span className="tab-icon">🎥</span>
                Generated Video
              </button>
            )}
            {isDone && (
              <button
                className={activeTab === "reports" ? "tab-active" : "tab"}
                onClick={() => setActiveTab("reports")}
              >
                <span className="tab-icon">📊</span>
                Reports
              </button>
            )}
          </div>

          {/* Violations tab */}
          {activeTab === "violations" && isDone && (
            <div className="rules-section">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <h3><span className="section-icon">📝</span> Violation Details</h3>
                <button className="report-btn pdf-btn" onClick={handleDownloadReport}>
                  📥 Download Report
                </button>
              </div>
              {violations.length === 0 ? (
                <p style={{ color: "#94a3b8" }}>No violations detected.</p>
              ) : (
                violations.map((v, i) => (
                  <div key={i} className={`rule-item priority-${(v.severity || "low").toLowerCase()}`}>
                    <div className="rule-number">#{i + 1}</div>
                    <div className="rule-content">
                      <div className="rule-text">
                        {v.description || v.event || JSON.stringify(v)}
                      </div>
                      <div className="rule-meta">
                        <span className="rule-category">
                          <span className="meta-icon">📌</span>
                          {v.rule_id || "Unknown rule"}
                        </span>
                        <span className={`rule-priority priority-${(v.severity || "low").toLowerCase()}`}>
                          {v.severity === "high" || v.severity === "critical" ? "🔴"
                            : v.severity === "medium" ? "🟡" : "🟢"}
                          {" "}{v.severity || "low"} — frame {v.frame ?? "?"}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Video tab */}
          {activeTab === "video" && videoJobId && (
            <div className="video-section">
              <div className="video-card">
                <div className="video-card-header">
                  <div className="video-icon">🤖</div>
                  <div>
                    <h3>AI-Generated Violation Video</h3>
                    <p>
                      {isDone
                        ? `Generated from analysis`
                        : "Loaded from VideoGenAgent output"}
                    </p>
                  </div>
                </div>
                <div className="video-container">
                  <video
                    controls
                    className="safety-video"
                    style={{ width: "100%", borderRadius: 8 }}
                    key={videoJobId}
                  >
                    <source
                      src={getSafeVideoUrl()}
                      type="video/mp4"
                    />
                    Your browser does not support the video tag.
                  </video>
                </div>
                <div className="video-actions">
                  <a
                    href={getSafeVideoUrl()}
                    download="generated_video.mp4"
                    className="download-video-btn"
                  >
                    📥 Download Video
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Reports tab - No job summary */}
          {activeTab === "reports" && isDone && (
            <div className="reports-section">
              <div className="reports-header">
                <h3><span className="section-icon">📊</span> Export Reports</h3>
                <p>Download violation data from the pipeline</p>
              </div>
              <div className="reports-grid">
                <div className="report-card">
                  <div className="report-icon">📄</div>
                  <h4>Violation Report</h4>
                  <p>Full AI-generated report from the rules agent</p>
                  <ul className="report-features">
                    <li>✓ All violations listed</li>
                    <li>✓ Severity breakdown</li>
                    <li>✓ Rule references</li>
                    <li>✓ Frame timestamps</li>
                  </ul>
                  <button
                    onClick={handleDownloadReport}
                    className="report-btn pdf-btn"
                  >
                    📥 Download Report
                  </button>
                </div>
                <div className="report-card">
                  <div className="report-icon">📊</div>
                  <h4>CSV Export</h4>
                  <p>Raw violations data — Excel compatible</p>
                  <ul className="report-features">
                    <li>✓ rule_id, severity, frame</li>
                    <li>✓ Filter & sort ready</li>
                    <li>✓ All violations included</li>
                  </ul>
                  <button
                    onClick={() => {
                      const rows = violations.map((v) =>
                        [v.rule_id || "-", v.severity || "-", v.frame ?? "-", v.description || ""].join(",")
                      );
                      const csv  = ["rule_id,severity,frame,description", ...rows].join("\n");
                      const blob = new Blob([csv], { type: "text/csv" });
                      const url  = URL.createObjectURL(blob);
                      const a    = document.createElement("a");
                      a.href     = url;
                      a.download = `violations_${Date.now()}.csv`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                    className="report-btn csv-btn"
                  >
                    📊 Export CSV
                  </button>
                </div>
              </div>
              {/* Job Summary section completely removed */}
            </div>
          )}
        </div>
      )}

      {/* Failed */}
      {isFailed && (
        <div className="status-message error" style={{ marginTop: 16 }}>
          <span>❌</span>
          <div>
            <strong>Pipeline failed</strong>
            {(job?.errors || []).map((e, i) => (
              <p key={i} style={{ margin: "4px 0 0" }}>{e}</p>
            ))}
          </div>
        </div>
      )}

      {/* Reset */}
      {(isDone || isFailed || manualJobId) && (
        <div className="reset-container">
          <button onClick={reset} className="reset-btn">
            <span>🔄</span> New Analysis
          </button>
        </div>
      )}

    </div>
  );
}

export default SafetyDashboard;